// screenshotService.js
// Client-side API service for trade screenshot uploads, retrieval, and deletion.

import { getApiUrl } from '../api/config';

const base = () => `${getApiUrl()}/screenshots`;

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('Not authenticated');
  return { Authorization: `Bearer ${token}` };
};

/**
 * Fetch all screenshots for a given trade.
 * @param {number|string} tradeId
 * @returns {Promise<Array>}
 */
export async function getTradeScreenshots(tradeId) {
  const res = await fetch(`${base()}/${tradeId}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to fetch screenshots: ${res.status}`);
  const json = await res.json();
  return json.data || [];
}

/**
 * Upload one or more screenshot files for a trade.
 * Accepts a FileList or array of File objects.
 * @param {number|string} tradeId
 * @param {File[]} files
 * @param {function} [onProgress] - optional callback(percent: number) — not supported with fetch; stubbed for API compat
 * @returns {Promise<Array>} saved screenshot records
 */
export async function uploadTradeScreenshots(tradeId, files, onProgress) {
  const formData = new FormData();
  Array.from(files).forEach((file) => {
    formData.append('screenshots', file);
  });

  // Note: do NOT set Content-Type manually for FormData — fetch sets it with the boundary automatically
  const res = await fetch(`${base()}/upload/${tradeId}`, {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });

  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error || `Upload failed: ${res.status}`);
  }

  if (onProgress) onProgress(100);

  const json = await res.json();
  return json.data || [];
}

/**
 * Update the screenshot_analysis text for a single screenshot.
 * @param {string} screenshotId - UUID
 * @param {string} analysis - new analysis text
 * @returns {Promise<void>}
 */
export async function updateScreenshotAnalysis(screenshotId, analysis) {
  const res = await fetch(`${base()}/${screenshotId}/analysis`, {
    method: 'PATCH',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ analysis }),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error || `Update failed: ${res.status}`);
  }
}

/**
 * Delete a single screenshot by its ID.
 * @param {string} screenshotId - UUID
 * @returns {Promise<void>}
 */
export async function deleteTradeScreenshot(screenshotId) {
  const res = await fetch(`${base()}/${screenshotId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error || `Delete failed: ${res.status}`);
  }
}
